
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, Bot, User, Loader2, Sparkles, MessageCircle } from 'lucide-react';
import { askRaedAlkhairAI } from '../services/aiService';
import { useStore } from '../context/StoreContext';

interface Message {
  id: string;
  text: string;
  sender: 'ai' | 'user';
  timestamp: Date;
}

interface AIChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AIChatDialog: React.FC<AIChatDialogProps> = ({ isOpen, onClose }) => {
  const { products, sales, warehouses, offerMembers, systemSettings } = useStore();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'أهلاً بك! أنا مساعد رائد الخير الذكي. كيف يمكنني مساعدتك اليوم بخصوص بيانات النظام أو استفساراتك؟',
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!inputText.trim() || isTyping) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    try {
      // Prepare context for AI
      const systemContext = {
        totalProducts: products.length,
        totalSales: sales.length,
        totalWarehouses: warehouses.length,
        totalMembers: (offerMembers || []).length,
        systemName: "رائد الخير",
        topProducts: products.slice(0, 5).map(p => ({ name: p.name, quantity: p.quantity })),
        recentSalesSummary: sales.slice(0, 5).map(s => ({ date: s.date, total: s.totalPriceUSD }))
      };

      const responseText = await askRaedAlkhairAI(userMessage.text, systemContext, {
        apiKey: systemSettings?.robot_api_key,
        instructions: systemSettings?.robot_instructions
      });
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error("AI Error:", error);
    } finally {
      setIsTyping(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-2xl h-[70vh] bg-white rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-slate-100"
        >
          {/* Header */}
          <div className="p-6 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-500 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                <Bot size={28} />
              </div>
              <div>
                <h3 className="text-xl font-black">مساعد رائد الخير الذكي</h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Active AI System</span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`flex ${message.sender === 'user' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`flex gap-3 max-w-[80%] ${message.sender === 'user' ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    message.sender === 'user' ? 'bg-indigo-100 text-indigo-600' : 'bg-primary-600 text-white'
                  }`}>
                    {message.sender === 'user' ? <User size={16} /> : <Bot size={16} />}
                  </div>
                  <div className={`p-4 rounded-2xl text-sm font-bold leading-relaxed shadow-sm ${
                    message.sender === 'user' 
                      ? 'bg-white text-slate-700 rounded-tr-none border border-slate-100' 
                      : 'bg-primary-600 text-white rounded-tl-none'
                  }`}>
                    {message.text}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-end">
                <div className="flex flex-row-reverse gap-3 max-w-[80%]">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-600 text-white">
                    <Bot size={16} />
                  </div>
                  <div className="p-4 bg-primary-600 text-white rounded-2xl rounded-tl-none shadow-sm flex items-center gap-2">
                    <Loader2 className="animate-spin" size={16} />
                    <span className="text-xs font-black">جاري التفكير...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-6 bg-white border-t border-slate-100">
            <div className="flex gap-4 p-2 bg-slate-50 rounded-2xl border border-slate-200 shadow-sm focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="اسألني أي شيء عن النظام أو الأرباح أو المخازن..."
                className="flex-1 bg-transparent border-none outline-none px-4 py-2 text-slate-700 placeholder:text-slate-400 font-bold"
                dir="rtl"
              />
              <button
                onClick={handleSend}
                disabled={!inputText.trim() || isTyping}
                className="w-12 h-12 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-300 text-white rounded-xl shadow-lg shadow-primary-500/20 flex items-center justify-center transition-all active:scale-95"
              >
                <Send size={20} className="rotate-180" />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-center gap-4 text-[10px] font-bold text-slate-400">
              <span className="flex items-center gap-1"><Sparkles size={10} /> مدعوم بتقنيات Gemini AI</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span>نظام رائد الخير الذكي 2026</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
